import { FormField } from "@comps/ui/form";
import { Input } from "@comps/ui/input";
import { SearchableSelect } from "@comps/ui/searchable-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@comps/ui/select";
import { ColorPicker } from "@comps/ui/ColorPicker";
import { FieldType, TYPE_ITEMS_BUTTON_COLOR, TYPE_ITEMS_BUTTON_CONFIRM_MSG, TYPE_ITEMS_BUTTON_ERROR_MSG, TYPE_ITEMS_BUTTON_LABEL, TYPE_ITEMS_BUTTON_MAPPING, TYPE_ITEMS_BUTTON_METHOD, TYPE_ITEMS_BUTTON_REFRESH, TYPE_ITEMS_BUTTON_REFRESH_ROW, TYPE_ITEMS_BUTTON_SUCCESS_MSG, TYPE_ITEMS_BUTTON_URL } from "@dto/fields";
import { WorkspaceColorType } from "@utils/colors";
import { useModelStore } from "src/store";
import { TW_CheckboxCell_not_selected, TW_CheckboxCell_selected, TW_FieldFormatSectionLine, TW_FieldFormatSectionLine_CheckboxInput, TW_FieldFormatSectionLine_Label, TW_FieldSection_Label, TW_PeriodConfigurationDiv } from "@utils/twClasses";
import { Plus, Trash2 } from "lucide-react";
import { CSSProperties, useState } from "react";
import { useTranslation } from "react-i18next";
import { FieldTypeItemsFormProps } from "./FieldTypeItemsForm";

const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH"];

interface MappingEntry {
    paramKey: string;
    fieldKey: string;
}

export function ButtonTypeItemsForm({ form, onTypeItemsChange }: FieldTypeItemsFormProps) {
    const { t } = useTranslation();
    const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
    const wsColor = useModelStore(state => state.wsColor);
    const wsLightColor = useModelStore(state => state.wsLightColor);
    const fields = (selectedObjectInfos?.fields ?? []).filter(f => f.type !== FieldType.Button);

    const [label, setLabel] = useState<string>(form.getValues("fieldTypeItems")?.get(TYPE_ITEMS_BUTTON_LABEL) ?? "");
    const [url, setUrl] = useState<string>(form.getValues("fieldTypeItems")?.get(TYPE_ITEMS_BUTTON_URL) ?? "");
    const [method, setMethod] = useState<string>(form.getValues("fieldTypeItems")?.get(TYPE_ITEMS_BUTTON_METHOD) ?? "GET");
    const [color, setColor] = useState<string | undefined>(form.getValues("fieldTypeItems")?.get(TYPE_ITEMS_BUTTON_COLOR));
    const [successMsg, setSuccessMsg] = useState<string>(form.getValues("fieldTypeItems")?.get(TYPE_ITEMS_BUTTON_SUCCESS_MSG) ?? "");
    const [errorMsg, setErrorMsg] = useState<string>(form.getValues("fieldTypeItems")?.get(TYPE_ITEMS_BUTTON_ERROR_MSG) ?? "");
    const [confirmMsg, setConfirmMsg] = useState<string>(form.getValues("fieldTypeItems")?.get(TYPE_ITEMS_BUTTON_CONFIRM_MSG) ?? "");
    const [refresh, setRefresh] = useState<boolean>(form.getValues("fieldTypeItems")?.get(TYPE_ITEMS_BUTTON_REFRESH) ?? false);
    const [refreshRow, setRefreshRow] = useState<boolean>(form.getValues("fieldTypeItems")?.get(TYPE_ITEMS_BUTTON_REFRESH_ROW) ?? false);
    const [mappings, setMappings] = useState<MappingEntry[]>(() => {
        const raw = form.getValues("fieldTypeItems")?.get(TYPE_ITEMS_BUTTON_MAPPING);
        if (!raw) return [];
        return Object.entries(raw as Record<string, string>).map(([paramKey, fieldKey]) => ({ paramKey, fieldKey }));
    });

    function updateItems(
        newLabel: string, newUrl: string, newMethod: string, newColor: string | undefined,
        newSuccessMsg: string, newErrorMsg: string, newConfirmMsg: string, newRefresh: boolean, newRefreshRow: boolean, newMappings: MappingEntry[]
    ) {
        const mappingObj: Record<string, string> = {};
        for (const entry of newMappings) {
            if (entry.paramKey) {
                mappingObj[entry.paramKey] = entry.fieldKey;
            }
        }
        const newItems = new Map<string, any>(form.getValues("fieldTypeItems"));
        newItems.set(TYPE_ITEMS_BUTTON_LABEL, newLabel);
        newItems.set(TYPE_ITEMS_BUTTON_URL, newUrl);
        newItems.set(TYPE_ITEMS_BUTTON_METHOD, newMethod);
        newItems.set(TYPE_ITEMS_BUTTON_MAPPING, mappingObj);
        newItems.set(TYPE_ITEMS_BUTTON_REFRESH, newRefresh);
        newItems.set(TYPE_ITEMS_BUTTON_REFRESH_ROW, newRefreshRow);
        if (newConfirmMsg) {
            newItems.set(TYPE_ITEMS_BUTTON_CONFIRM_MSG, newConfirmMsg);
        } else {
            newItems.delete(TYPE_ITEMS_BUTTON_CONFIRM_MSG);
        }
        if (newColor) {
            newItems.set(TYPE_ITEMS_BUTTON_COLOR, newColor);
        } else {
            newItems.delete(TYPE_ITEMS_BUTTON_COLOR);
        }
        if (newSuccessMsg) {
            newItems.set(TYPE_ITEMS_BUTTON_SUCCESS_MSG, newSuccessMsg);
        } else {
            newItems.delete(TYPE_ITEMS_BUTTON_SUCCESS_MSG);
        }
        if (newErrorMsg) {
            newItems.set(TYPE_ITEMS_BUTTON_ERROR_MSG, newErrorMsg);
        } else {
            newItems.delete(TYPE_ITEMS_BUTTON_ERROR_MSG);
        }
        form.setValue("fieldTypeItems", newItems);
        onTypeItemsChange(newItems);
    }

    function onLabelChange(e: React.ChangeEvent<HTMLInputElement>) {
        setLabel(e.target.value);
        updateItems(e.target.value, url, method, color, successMsg, errorMsg, confirmMsg, refresh, refreshRow, mappings);
    }

    function onUrlChange(e: React.ChangeEvent<HTMLInputElement>) {
        setUrl(e.target.value);
        updateItems(label, e.target.value, method, color, successMsg, errorMsg, confirmMsg, refresh, refreshRow, mappings);
    }

    function onMethodChange(value: string) {
        setMethod(value);
        updateItems(label, url, value, color, successMsg, errorMsg, confirmMsg, refresh, refreshRow, mappings);
    }

    function onColorChange(newColor: string) {
        setColor(newColor);
        updateItems(label, url, method, newColor, successMsg, errorMsg, confirmMsg, refresh, refreshRow, mappings);
    }

    function onSuccessMsgChange(e: React.ChangeEvent<HTMLInputElement>) {
        setSuccessMsg(e.target.value);
        updateItems(label, url, method, color, e.target.value, errorMsg, confirmMsg, refresh, refreshRow, mappings);
    }

    function onErrorMsgChange(e: React.ChangeEvent<HTMLInputElement>) {
        setErrorMsg(e.target.value);
        updateItems(label, url, method, color, successMsg, e.target.value, confirmMsg, refresh, refreshRow, mappings);
    }

    function onConfirmMsgChange(e: React.ChangeEvent<HTMLInputElement>) {
        setConfirmMsg(e.target.value);
        updateItems(label, url, method, color, successMsg, errorMsg, e.target.value, refresh, refreshRow, mappings);
    }

    function onRefreshChange(e: React.ChangeEvent<HTMLInputElement>) {
        const checked = e.target.checked;
        setRefresh(checked);
        updateItems(label, url, method, color, successMsg, errorMsg, confirmMsg, checked, refreshRow, mappings);
    }

    function onRefreshRowChange(e: React.ChangeEvent<HTMLInputElement>) {
        const checked = e.target.checked;
        setRefreshRow(checked);
        updateItems(label, url, method, color, successMsg, errorMsg, confirmMsg, refresh, checked, mappings);
    }

    function onMappingParamKeyChange(index: number, value: string) {
        const newMappings = mappings.map((m, i) => i === index ? { ...m, paramKey: value } : m);
        setMappings(newMappings);
        updateItems(label, url, method, color, successMsg, errorMsg, confirmMsg, refresh, refreshRow, newMappings);
    }

    function onMappingFieldKeyChange(index: number, value: string) {
        const newMappings = mappings.map((m, i) => i === index ? { ...m, fieldKey: value } : m);
        setMappings(newMappings);
        updateItems(label, url, method, color, successMsg, errorMsg, confirmMsg, refresh, refreshRow, newMappings);
    }

    function addMapping() {
        const newMappings = [...mappings, { paramKey: "", fieldKey: "" }];
        setMappings(newMappings);
        updateItems(label, url, method, color, successMsg, errorMsg, confirmMsg, refresh, refreshRow, newMappings);
    }

    function removeMapping(index: number) {
        const newMappings = mappings.filter((_, i) => i !== index);
        setMappings(newMappings);
        updateItems(label, url, method, color, successMsg, errorMsg, confirmMsg, refresh, refreshRow, newMappings);
    }

    const scrollbarStyle: CSSProperties = {
        scrollbarColor: `${wsColor} ${wsLightColor}`,
    };

    return (
        <FormField
            control={form.control}
            name="fieldTypeItems"
            render={() => (
                <div className="max-h-96 overflow-y-auto pr-1 w-full scrollbar-thin scrollbar-thumb-compozz-purple scrollbar-track-compozz-purple-light scrollbar-button-hidden" style={scrollbarStyle}>
                    <div className={TW_PeriodConfigurationDiv}>
                        {/* Label */}
                        <p className={TW_FieldSection_Label}>{t("field.button.parameter.title")}</p>

                        <div className={TW_FieldFormatSectionLine}>
                            <p className={TW_FieldFormatSectionLine_Label + " w-1/2"}>{t("field.button.label.title")}</p>
                            <Input
                                placeholder={t("field.button.label.placeholder")}
                                value={label}
                                onChange={onLabelChange}
                                className="w-1/2"
                            />
                        </div>

                        {/* URL */}
                        <div className={TW_FieldFormatSectionLine}>
                            <p className={TW_FieldFormatSectionLine_Label + " w-1/2"}>{t("field.button.url.title")}</p>
                            <Input
                                placeholder={t("field.button.url.placeholder")}
                                value={url}
                                onChange={onUrlChange}
                                className="w-1/2"
                            />
                        </div>

                        {/* Méthode HTTP */}
                        <div className={TW_FieldFormatSectionLine}>
                            <p className={TW_FieldFormatSectionLine_Label + " w-1/2"}>{t("field.button.method.title")}</p>
                            <Select value={method} onValueChange={onMethodChange}>
                                <SelectTrigger className="w-1/2">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {HTTP_METHODS.map(m => (
                                        <SelectItem key={m} value={m}>{m}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Couleur */}
                        <div className={TW_FieldFormatSectionLine}>
                            <p className={TW_FieldFormatSectionLine_Label + " w-1/2"}>{t("field.button.color.title")}</p>
                            <div className="flex justify-center items-center gap-4 w-2/3">

                                {color && color !== wsColor && (
                                    <span
                                        className="text-xs text-gray-400 cursor-pointer hover:text-gray-700"
                                        onClick={() => { setColor(undefined); updateItems(label, url, method, undefined, successMsg, errorMsg, confirmMsg, refresh, refreshRow, mappings); }}
                                    >
                                        {t("field.button.color.reset")}
                                    </span>
                                )}
                                <ColorPicker
                                    color={color ?? wsColor}
                                    onChange={onColorChange}
                                    colorType={WorkspaceColorType}
                                    className="w-8 h-8"
                                />
                            </div>
                        </div>

                        {/* Message de succès */}
                        <div className={TW_FieldFormatSectionLine}>
                            <p className={TW_FieldFormatSectionLine_Label + " w-1/2"}>{t("field.button.success.msg.title")}</p>
                            <Input
                                placeholder={t("field.button.success.msg.placeholder")}
                                value={successMsg}
                                onChange={onSuccessMsgChange}
                                className="w-1/2"
                            />
                        </div>

                        {/* Message d'erreur */}
                        <div className={TW_FieldFormatSectionLine}>
                            <p className={TW_FieldFormatSectionLine_Label + " w-1/2"}>{t("field.button.error.msg.title")}</p>
                        <Input
                            placeholder={t("field.button.error.msg.placeholder")}
                            value={errorMsg}
                            onChange={onErrorMsgChange}
                                className="w-1/2"
                            />
                        </div>

                        {/* Message de confirmation avant exécution */}
                        <div className={TW_FieldFormatSectionLine}>
                            <p className={TW_FieldFormatSectionLine_Label + " w-1/2"}>{t("field.button.confirm.msg.title")}</p>
                            <Input
                                placeholder={t("field.button.confirm.msg.placeholder")}
                                value={confirmMsg}
                                onChange={onConfirmMsgChange}
                                className="w-1/2"
                            />
                        </div>

                        {/* Rafraîchir la ligne après exécution */}
                        <div className={TW_FieldFormatSectionLine + " mt-2"}>
                            <p className={TW_FieldFormatSectionLine_Label + " w-1/2"}>{t("field.button.refresh.row.title")}</p>
                            <Input className={(refreshRow ? TW_CheckboxCell_selected : TW_CheckboxCell_not_selected)} type="checkbox" checked={refreshRow} onChange={onRefreshRowChange} />
                        </div>

                        {/* Rafraîchir après exécution */}
                        <div className={TW_FieldFormatSectionLine + " mt-2"}>
                            <p className={TW_FieldFormatSectionLine_Label + " w-1/2"}>{t("field.button.refresh.title")}</p>
                            <Input className={(refresh ? TW_CheckboxCell_selected : TW_CheckboxCell_not_selected)} type="checkbox" checked={refresh} onChange={onRefreshChange} />
                        </div>

                        {/* Mapping */}
                        <p className={TW_FieldSection_Label + " mt-4"}>{t("field.button.mapping.title")}</p>
                        <div className="flex flex-col gap-2 w-full">
                            {mappings.map((entry, index) => (
                                <div key={index} className="flex flex-row items-center gap-2 w-full">
                                    <Input
                                        placeholder={t("field.button.mapping.key.placeholder")}
                                        value={entry.paramKey}
                                        onChange={(e) => onMappingParamKeyChange(index, e.target.value)}
                                        className="flex-1"
                                    />
                                    <SearchableSelect
                                        className="flex-1"
                                        placeholder={t("field.button.mapping.column.placeholder")}
                                        value={entry.fieldKey}
                                        onValueChange={(value) => onMappingFieldKeyChange(index, value)}
                                        options={fields.map(f => ({ value: f._key, label: f.name }))}
                                    />
                                    <Trash2
                                        className="w-4 h-4 text-gray-500 cursor-pointer hover:text-red-500 shrink-0"
                                        onClick={() => removeMapping(index)}
                                    />
                                </div>
                            ))}
                            <div
                                className="flex flex-row items-center gap-1 cursor-pointer text-gray-500 hover:text-gray-800 text-sm"
                                onClick={addMapping}
                            >
                                <Plus className="w-4 h-4" />
                                <span>{t("field.button.mapping.add")}</span>
                            </div>
                        </div>

                    </div>
                </div>
            )}
        />
    );
}
